import moment from "moment";
import React, { useEffect, useRef } from "react";
import { StrategyEntityItemType } from "../../services/strategyMap";
import GTGridLayoutStrategyLayout from "./GTGridLayoutStrategyLayout";
import GTStrategyMap from "./GTStrategyMap";
import { ViewType } from "./types";

export const GT_spacing = 75;
export const GT_taskCardHeight = 100;
export const GT_bottomSpacing = 50;
export const GT_progressBarTopInPx = 20;
export const GT_tasksTopInPx = 150 + GT_progressBarTopInPx;
export const GT_progressBarAndTasksGapInPx = 30;

export const getSpacing = (
  viewType: ViewType,
  containerWidth: number,
  startDate?: string,
) => {
  const totalMargin = 0; // can be adjusted in future
  const availableWidth = containerWidth - totalMargin;

  if (viewType === "weekly") {
    return Math.round(availableWidth / 7);
  }
  if (viewType === "monthly") {
    return Math.round(availableWidth / 24);
  }
  if (viewType === "quarterly") {
    return Math.round(availableWidth / 69);
  }
  if (viewType === "yearly") {
    const start = moment(startDate).startOf("year");
    const end = moment(startDate).endOf("year");
    const totalDays = end.diff(start, "days") + 1;

    return Math.round(availableWidth / totalDays);
  }

  return GT_spacing;
};

interface GanttViewTimelineProps {
  simFlag: boolean;
  viewType: ViewType;
  orgId: string;
  onProjectClick: (project: any) => void;
  onTaskClick: (task: any) => void;
  singleRoadmapRes: StrategyEntityItemType;
  idForScroll?: string;
  dependecyView: boolean;
}

export const GanttViewStrategyMapTimeline: React.FC<GanttViewTimelineProps> = ({
  orgId,
  simFlag = false,
  viewType = "weekly",
  onProjectClick,
  onTaskClick,
  singleRoadmapRes,
  idForScroll = "",
  dependecyView,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const simulation = simFlag;

  const calculateSpacing = () => {
    if (!containerRef.current) return GT_spacing;

    const startDate = singleRoadmapRes?.start_date;

    return getSpacing(viewType, containerRef.current.offsetWidth, startDate);
  };

  const currentSpacing = calculateSpacing();

  const getStartDate = () => {
    if (simulation) {
      return singleRoadmapRes?.sim_data?.start_date;
    }

    return singleRoadmapRes?.start_date;
  };

  const getEndDate = () => {
    if (simulation) {
      return singleRoadmapRes?.sim_data?.end_date;
    }

    return singleRoadmapRes?.end_date;
  };

  const scrollToTimelineItem = (
    scrollId: string,
    shouldBeDelayed: boolean = false,
  ) => {
    const scrollTimeMs = shouldBeDelayed ? 600 : 100;
    const highlightTimeMs = shouldBeDelayed ? 600 : 400;
    const highlightRemoveTimeMs = 3000;

    setTimeout(() => {
      const element = document.getElementById(`project-${scrollId}`);
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "start",
        });

        setTimeout(() => {
          element.classList.add("timeline-item-highlight");
          const removeClass = () => {
            element.classList.remove("timeline-item-highlight");
          };
          element.addEventListener("animationend", removeClass, { once: true });
          setTimeout(removeClass, highlightRemoveTimeMs);
        }, highlightTimeMs);
      }
    }, scrollTimeMs);
  };

  useEffect(() => {
    const scrollId = idForScroll;
    if (!scrollId) return;

    scrollToTimelineItem(scrollId);
  }, [idForScroll]);

  useEffect(() => {
    const scrollId = idForScroll;
    if (!scrollId) return;

    scrollToTimelineItem(scrollId, true);
  }, [viewType]);

  console.log("Re-Rendered StrategyMapTimeLine.tsx");

  return (
    <div
      ref={containerRef}
      className={`relative h-full w-full overflow-scroll`}
    >
      <GTGridLayoutStrategyLayout
        orgId={orgId}
        viewType={viewType}
        spacing={currentSpacing}
        entityLength={singleRoadmapRes?.entities?.length}
        startDate={getStartDate()}
        endDate={getEndDate()}
        milestones={singleRoadmapRes?.milestones}
      />

      <div
        className="relative "
        style={{ top: GT_tasksTopInPx, width: "fit-content" }}
      >
        {singleRoadmapRes?.entities?.map((entity, index) => {

          const entityWithoutInvalidDependencies = {
            ...entity,
            dependencies: entity.dependencies.filter((d) => !d.invalid_dependency),
          };
          return (
            <div
              className={`smapbox-${entity.id}`}
              key={`project-${entity?.id ?? index}`}
            >
              <GTStrategyMap
                key={index}
                orgId={orgId}
                project={entityWithoutInvalidDependencies}
                index={index}
                handleProjectClick={onProjectClick}
                startDate={singleRoadmapRes?.start_date}
                isTaskHighlighted={false}
                viewType={viewType}
                simFlag={simFlag}
                spacing={currentSpacing}
                dependecyView={dependecyView} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

